import { Component, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { SERVICE_POLICY_EXTENSION_SERVICE } from 'ziti-console-lib';
import { ApiService } from '@netfoundry-ui/shared/services';
import { firstValueFrom, Subscription } from 'rxjs';
import { cloneDeep } from 'lodash';
import { ServicePoliciesExtensionService } from '../service-policies-extension/service-policies-extension.service';
import { ActivatedRoute, Router } from '@angular/router';

type PolicyConflictWarning = {
    identityRoles: string[];
    serviceRoles: string[];
    semantic: string;
    policyType: string;
    conflicted: boolean;
    resolvesExistingConflict: boolean;
};

@Component({
    selector: 'netfoundry-ui-service-policy-form-extension',
    templateUrl: './service-policy-form-extension.component.html',
    styleUrls: ['./service-policy-form-extension.component.scss'],
    standalone: false,
})
export class ServicePolicyFormExtensionComponent implements OnInit, OnDestroy {
    private readonly conflictEntityLabel = 'policy';
    subscription = new Subscription();
    currentNetwork: any = {};
    formData: any = {};
    conflictData: any = {};
    conflictDetails: any = {};
    conflictDetailsLoading = false;
    attributeConflictDetails: any = {};
    attributeConflictWarning: PolicyConflictWarning | undefined;

    @ViewChild('servicePolicyForm') servicePolicyForm: any;

    constructor(
        @Inject(SERVICE_POLICY_EXTENSION_SERVICE) public svc: ServicePoliciesExtensionService,
        private apiService: ApiService,
        private router: Router,
        private route: ActivatedRoute,
    ) {
    }

    ngOnInit() {
        this.subscription.add(
            this.svc.extensionEvent.subscribe((event: any) => {
                if (event?.type === 'formDataUpdated' && event?.data?.entityType === 'service-policy') {
                    const nextFormData = event?.data?.formData || {};
                    this.formData = nextFormData;
                    this.attributeConflictWarning = undefined;
                    this.attributeConflictDetails = {};
                    this.getConflictData();
                    return;
                }

                const isRelevantType = [
                    'service-attributes-changed',
                    'identity-attributes-changed',
                    'policy-type-changed',
                    'policy-semantic-changed'
                  ].includes(event?.type);
                  const isServicePolicy = event?.data?.entityType === 'service-policy';
                  if (isRelevantType && isServicePolicy) {
                    this.handleConflictInputChange(event?.data);
                  }
            })
        );

        this.subscription.add(
            this.apiService.currentNetwork.subscribe((currentNetwork: any) => {
                this.currentNetwork = currentNetwork;
                this.getConflictData();
            })
        );
    }

    getConflictData() {
        if (!this.currentNetwork?.id || !this.formData?.id) {
            this.conflictData = {};
            this.resetConflictDetails();
            this.updateConflictBadge();
            return;
        }

        firstValueFrom(this.svc.getPolicyConflicts(this.currentNetwork.id, [this.formData.id])).then((result: any) => {
            this.conflictData = result?._embedded?.policyConflictList?.[0] || {};
            if (!this.conflictData?.conflicted) {
                this.resetConflictDetails();
            }
            this.updateConflictBadge();
            if (this.conflictData?.conflicted) {
                this.getConflictDetails(this.formData.id);
            }
        }).catch(() => {
            this.conflictData = {};
            this.resetConflictDetails();
            this.updateConflictBadge();
        });
    }

    getConflictDetails(
        servicePolicyId?: string,
        identityRoles: string[] = [],
        serviceRoles: string[] = [],
        semantic?: string,
        policyType?: string
    ) {
        const detailIdentityRoles = Array.isArray(identityRoles) ? identityRoles : [];
        const detailServiceRoles = Array.isArray(serviceRoles) ? serviceRoles : [];
        const shouldUseAttributes = detailIdentityRoles.length > 0 || detailServiceRoles.length > 0;
        const detailPolicyId =
            !shouldUseAttributes && servicePolicyId !== undefined
                ? servicePolicyId
                : (!shouldUseAttributes ? (this.formData?.id || '') : '');
        if (!this.currentNetwork?.id) {
            this.resetConflictDetails();
            return Promise.resolve({});
        }

        this.conflictDetailsLoading = true;
        return firstValueFrom(
            this.svc.getPolicyConflictDetails(
                this.currentNetwork.id,
                detailPolicyId,
                detailIdentityRoles,
                detailServiceRoles,
                semantic || this.formData?.semantic || 'AnyOf',
                policyType || this.formData?.type || 'Bind'
            )
        )
            .then((result: any) => {
                this.conflictDetails = result || {};
                this.svc.conflictDetails = this.conflictDetails;
                return this.conflictDetails;
            })
            .catch(() => {
                this.resetConflictDetails();
                return {};
            })
            .finally(() => {
                this.conflictDetailsLoading = false;
            });
    }

    handleConflictInputChange(change: any) {
        if (!this.currentNetwork?.id) {
            this.attributeConflictWarning = undefined;
            this.attributeConflictDetails = {};
            this.updateConflictBadge();
            return;
        }

        const identityRoles = Array.isArray(change?.identityRoles) ? change.identityRoles : [];
        const serviceRoles = Array.isArray(change?.serviceRoles) ? change.serviceRoles : [];
        const semantic = change?.semantic || this.formData?.semantic || 'AnyOf';
        const policyType = change?.policyType || this.formData?.type || 'Bind';
        const hasIdentityRoles = identityRoles.some((role: string) => !!role?.trim());
        const hasServiceRoles = serviceRoles.some((role: string) => !!role?.trim());

        if (!hasIdentityRoles || !hasServiceRoles) {
            this.attributeConflictWarning = this.hasConflict ? {
                identityRoles,
                serviceRoles,
                semantic,
                policyType,
                conflicted: false,
                resolvesExistingConflict: true,
            } : undefined;
            this.attributeConflictDetails = {};
            this.updateConflictBadge();
            return;
        }

        firstValueFrom(
            this.svc.getPolicyAttributeConflict(this.currentNetwork.id, identityRoles, serviceRoles, semantic, policyType)
        ).then((result: any) => {
            if (result?.conflicted) {
                this.attributeConflictWarning = {
                    identityRoles,
                    serviceRoles,
                    semantic,
                    policyType,
                    conflicted: true,
                    resolvesExistingConflict: false,
                };
                this.attributeConflictDetails = {};
                this.updateConflictBadge();
                this.getConflictDetails(undefined, identityRoles, serviceRoles, semantic, policyType).then((details: any) => {
                    this.attributeConflictDetails = details || {};
                });
                return;
            }

            this.attributeConflictWarning = this.hasConflict ? {
                identityRoles,
                serviceRoles,
                semantic,
                policyType,
                conflicted: false,
                resolvesExistingConflict: true,
            } : undefined;
            this.attributeConflictDetails = {};
            this.updateConflictBadge();
        }).catch(() => {
            this.attributeConflictWarning = undefined;
            this.attributeConflictDetails = {};
            this.updateConflictBadge();
        });
    }

    openConflictDetailsModal(event?: Event) {
        event?.preventDefault();
        event?.stopPropagation();

        if (!this.showConflictBanner || this.attributeConflictWarning?.resolvesExistingConflict) {
            return;
        }

        this.svc.conflictPageData = {
            title: 'Potential Conflicts',
            subtitle: this.conflictBannerMessage,
            conflictDetails: this.getModalConflictDetails(),
            identityRoles: this.attributeConflictWarning?.identityRoles || [],
            serviceRoles: this.attributeConflictWarning?.serviceRoles || [],
            semantic: this.attributeConflictWarning?.semantic || this.formData?.semantic,
            policyType: this.attributeConflictWarning?.policyType || this.formData?.type,
            emptyStateMessage: this.genericEmptyStateMessage,
        };
        this.router.navigate(['conflict'], {
            relativeTo: this.route,
            state: {
                fromConflictSourceForm: true,
            },
        });
    }

    getModalConflictDetails() {
        if (this.attributeConflictWarning?.conflicted && this.attributeConflictDetails?.identities?.length > 0) {
            return this.attributeConflictDetails;
        }

        if (this.conflictDetails?.identities?.length > 0) {
            return this.conflictDetails;
        }

        return this.attributeConflictDetails || this.conflictDetails || {};
    }

    get conflictBannerMessage() {
        if (this.attributeConflictWarning?.resolvesExistingConflict) {
            return this.conflictResolutionMessage;
        }

        if (this.attributeConflictWarning?.conflicted) {
            return this.previewConflictMessage;
        }

        if (this.hasConflict) {
            return this.existingConflictMessage;
        }

        return '';
    }

    getConflictTooltip() {
        if (this.attributeConflictWarning?.resolvesExistingConflict) {
            return this.conflictResolutionMessage;
        }

        if (this.attributeConflictWarning?.conflicted) {
            return this.previewConflictMessage;
        }

        if (this.hasConflict) {
            return this.existingConflictMessage;
        }

        return '';
    }

    updateConflictBadge() {
        const activeFormData = this.formData || this.svc?.formData || {};
        if (!Array.isArray(activeFormData.badges)) {
            activeFormData.badges = [];
        }

        const hasConflictBadge = activeFormData.badges.some((badge: any) => badge.id === 'conflicted');
        if (!hasConflictBadge && this.shouldShowConflictBadge) {
            activeFormData.badges.push({ label: 'Potential Conflict Detected', class: 'warning', id: 'conflicted' });
        } else if (hasConflictBadge && !this.shouldShowConflictBadge) {
            activeFormData.badges = activeFormData.badges.filter((badge: any) => badge.id !== 'conflicted');
        }

        this.formData = activeFormData;
        this.svc.formData = activeFormData;
        this.syncBadgeBaseline(activeFormData.badges);
    }

    private syncBadgeBaseline(badges: any[]) {
        if (!this.servicePolicyForm?.initData) {
            return;
        }

        this.servicePolicyForm.initData.badges = cloneDeep(badges || []);
    }

    resetConflictDetails() {
        this.conflictDetails = {};
        this.conflictDetailsLoading = false;
    }

    get showConflictBanner() {
        return this.hasConflict || !!this.attributeConflictWarning;
    }

    get hasConflict() {
        return !!this.conflictData?.conflicted;
    }

    get shouldShowConflictBadge() {
        return this.hasConflict && !this.attributeConflictWarning?.resolvesExistingConflict;
    }

    private get genericConflictMessage() {
        return this.attributeConflictWarning?.conflicted ? this.previewConflictMessage : this.existingConflictMessage;
    }

    private get existingConflictMessage() {
        return 'This policy may have conflicts.';
    }

    private get previewConflictMessage() {
        return 'This policy may have potential conflicts. Saving these changes could result potential conflicts.';
    }
      
    private get conflictResolutionMessage() {
        return `This policy has potential conflicts. Saving these changes may help resolve them.`;
    }
      
    private get genericEmptyStateMessage() {
        return `This policy may have potential conflicts, but detailed information is not available at this time.`;
    }

    ngOnDestroy() {
        this.subscription.unsubscribe();
    }
}
